/* VØRIX Premium — product page module (theme v4) */

const qs = (selector, scope = document) => scope.querySelector(selector);
const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

const ready = (fn) => (window.vorix?.api ? fn() : document.addEventListener('vorix:ready', fn, { once: true }));

const start = (scope = document) => {
  const { api, strings = {}, settings = {} } = window.vorix;
  qsa('[data-product]', scope).forEach((section) => {
    if (section.dataset.vxInit) return;
    section.dataset.vxInit = '1';
    initProduct(section, api, strings, settings);
  });
};
ready(() => start());

// Theme editor: a re-rendered product section is new markup that needs wiring again.
document.addEventListener('shopify:section:load', (event) => ready(() => start(event.target)));

function initProduct(section, api, strings, settings) {
  initGallery(section);
  initLightbox(section, api);
  initSizeGuide(section);
  const form = qs('[data-product-form]', section);
  if (form) initForm(section, form, api, strings, settings);
  initColours(section, form, api);
  initAmountGallery(section, form);
  initSet(section, form, api, strings);
  initBuybar(section, form);

  document.addEventListener('vorix:zero-hour', () => {
    qsa('[data-save-label][data-label-open]', section).forEach((label) => {
      label.dataset.labelOff = label.dataset.labelOpen;
      if (label.closest('[aria-pressed="false"]')) label.textContent = label.dataset.labelOpen;
    });
    window.dispatchEvent(new Event('scroll'));
  });

  qsa('[data-open-accordion]', section).forEach((btn) =>
    btn.addEventListener('click', () => {
      const acc = qs(`[data-accordion="${btn.dataset.openAccordion}"]`, section);
      if (!acc) return;
      acc.open = true;
      acc.scrollIntoView({ behavior: api.motion() === 'off' ? 'auto' : 'smooth', block: 'start' });
      qs('summary', acc)?.focus({ preventScroll: true });
    }),
  );
}

/* ---------- Gallery: native scroll-snap on mobile, editorial grid on desktop ---------- */
function initGallery(section) {
  const track = qs('[data-gallery-track]', section);
  const current = qs('[data-gallery-current]', section);
  const bar = qs('[data-gallery-bar]', section);
  if (!track) return;
  let frame = 0;
  const update = () => {
    frame = 0;
    // Only the chosen colour's photos are shown, so count what is visible now.
    const count = qsa('[data-gallery-item]:not([hidden])', track).length;
    const width = track.clientWidth || 1;
    const index = Math.min(count - 1, Math.max(0, Math.round(track.scrollLeft / width)));
    if (current) current.textContent = String(index + 1);
    if (bar) bar.style.setProperty('--progress', String(index));
  };
  track.addEventListener('scroll', () => (frame ||= requestAnimationFrame(update)), { passive: true });
  track.addEventListener('keydown', (event) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    if (track.scrollWidth <= track.clientWidth) return;
    event.preventDefault();
    const smooth = window.vorix?.api?.motion() !== 'off';
    track.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
  });
}

/* ---------- Lightbox ---------- */
function initLightbox(section, api) {
  const dialog = qs('[data-lightbox]', section);
  if (!dialog) return;
  const track = qs('[data-lightbox-track]', dialog);
  const allSlides = qsa('[data-lightbox-slide]', dialog);
  // Slides of other colours are hidden, so navigation only ever counts the visible ones.
  const visibleSlides = () => allSlides.filter((s) => !s.hidden);
  const counter = qs('[data-lightbox-current]', dialog);
  let opener = null;

  const load = (i) => {
    const slide = visibleSlides()[i];
    const img = slide && qs('img', slide);
    if (img && !img.src) {
      img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
    }
  };
  const indexNow = () => Math.round(track.scrollLeft / (track.clientWidth || 1));
  const go = (i, smooth = true) => {
    const count = visibleSlides().length || 1;
    const index = (i + count) % count;
    [index - 1, index, index + 1].forEach((n) => load((n + count) % count));
    track.scrollTo({ left: index * track.clientWidth, behavior: smooth && api.motion() !== 'off' ? 'smooth' : 'auto' });
    if (counter) counter.textContent = String(index + 1);
  };
  const close = () => {
    dialog.classList.remove('is-open');
    allSlides.forEach((s) => s.classList.remove('is-zoomed'));
    setTimeout(
      () => {
        dialog.close();
        document.documentElement.classList.remove('is-locked');
        opener?.focus({ preventScroll: true });
      },
      api.motion() === 'off' ? 0 : 240,
    );
  };

  qsa('[data-zoom]', section).forEach((btn) =>
    btn.addEventListener('click', () => {
      opener = btn;
      const shown = qsa('[data-gallery-item]', section).filter((item) => !item.hidden);
      const start = Math.max(0, shown.indexOf(btn.closest('[data-gallery-item]')));
      dialog.showModal();
      document.documentElement.classList.add('is-locked');
      requestAnimationFrame(() => {
        dialog.classList.add('is-open');
        go(start, false);
        track.focus({ preventScroll: true });
      });
    }),
  );
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });
  qs('[data-close]', dialog)?.addEventListener('click', close);
  qs('[data-lightbox-prev]', dialog)?.addEventListener('click', () => go(indexNow() - 1));
  qs('[data-lightbox-next]', dialog)?.addEventListener('click', () => go(indexNow() + 1));
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') go(indexNow() + 1);
    if (event.key === 'ArrowLeft') go(indexNow() - 1);
  });
  let frame = 0;
  track.addEventListener(
    'scroll',
    () =>
      (frame ||= requestAnimationFrame(() => {
        frame = 0;
        const slides = visibleSlides();
        const i = indexNow();
        if (counter) counter.textContent = String(i + 1);
        load(i + 1 < slides.length ? i + 1 : i);
        slides.forEach((s, n) => n !== i && s.classList.remove('is-zoomed'));
      })),
    { passive: true },
  );
  // Tap to zoom around the tapped point.
  allSlides.forEach((slide) =>
    slide.addEventListener('click', (event) => {
      const img = qs('img', slide);
      if (!img) return;
      const rect = img.getBoundingClientRect();
      img.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
      slide.classList.toggle('is-zoomed');
    }),
  );
}

/* ---------- Size guide units ---------- */
function initSizeGuide(section) {
  qsa('[data-size-guide]', section).forEach((guide) => {
    const buttons = qsa('[data-unit]', guide);
    const cells = qsa('[data-cm]', guide);
    buttons.forEach((btn) =>
      btn.addEventListener('click', () => {
        const inches = btn.dataset.unit === 'in';
        buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        const caption = qs('[data-size-caption]', guide);
        if (caption) caption.textContent = `Garment measurements in ${inches ? 'inches' : 'centimetres'}`;
        cells.forEach((cell) => {
          const cm = Number(cell.dataset.cm);
          cell.textContent = inches ? (cm / 2.54).toFixed(1).replace(/\.0$/, '') : String(cm);
        });
      }),
    );
  });
}

/* ---------- Variant picker + add to bag ---------- */
function initForm(section, form, api, strings, settings) {
  const variants = JSON.parse(qs('[data-variants]', form)?.textContent || '[]');
  const idInput = qs('[data-variant-id]', form);
  const button = qs('[data-buy-button]', form);
  const label = qs('[data-buy-label]', form);
  const error = qs('[data-form-error]', form);
  // The print picker sits with the options but links to another product, so it isn't one of this variant's options.
  const fieldsets = qsa('fieldset.opt:not([data-print-picker])', form);
  const isDefault = idInput?.hasAttribute('data-default');
  if (idInput && !idInput.name) idInput.name = 'id';

  let money;
  try {
    money = new Intl.NumberFormat(settings.locale || 'en-AU', { style: 'currency', currency: settings.currency || 'AUD' });
  } catch (_) {
    money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: settings.currency || 'AUD' });
  }
  let prices = {};
  try {
    prices = JSON.parse(qs('[data-prices]', form)?.textContent || '{}');
  } catch (_) {
    /* fall back to Intl formatting */
  }

  const selectedOptions = () =>
    fieldsets.map((fs) => {
      const fixed = qs('[data-fixed-option]', fs);
      if (fixed) return fixed.value;
      return qs('input[type="radio"]:checked', fs)?.value ?? null;
    });

  const currentVariant = () => {
    if (isDefault) return variants[0];
    const chosen = selectedOptions();
    if (chosen.some((v) => v === null)) return null;
    return variants.find((v) => v.options.every((value, i) => value === chosen[i])) || null;
  };

  const setLabel = (text) => qsa('[data-buy-label]', section).forEach((el) => (el.textContent = text));

  const render = () => {
    const variant = currentVariant();
    fieldsets.forEach((fs) => {
      const checked = qs('input[type="radio"]:checked', fs);
      const out = qs('[data-option-value]', fs);
      if (out && checked) out.textContent = checked.value;
      fs.classList.remove('is-missing');
    });
    if (error) error.hidden = true;
    if (!variant) {
      const allChosen = selectedOptions().every((v) => v !== null);
      button.disabled = allChosen;
      button.dataset.state = allChosen ? 'unavailable' : 'choose';
      setLabel(allChosen ? strings.unavailable || 'Unavailable' : strings.selectSize || 'Select a size');
      return;
    }
    idInput.value = variant.id;
    button.dataset.state = variant.available ? 'ready' : 'sold-out';
    button.disabled = !variant.available;
    setLabel(variant.available ? strings.addToBag : strings.soldOut);
    const formatted = prices[variant.id] || {};
    const onSale = variant.compare_at_price > variant.price;
    qsa('[data-product-price]', section).forEach((wrap) => {
      const priceEl = qs('.price', wrap);
      priceEl?.classList.toggle('price--sale', onSale);
      const now = qs('.price__now', wrap);
      if (now) now.textContent = formatted.price || money.format(variant.price / 100);
      const was = qs('.price__was', wrap);
      if (was) {
        was.hidden = !onSale;
        if (onSale) was.textContent = formatted.compare || money.format(variant.compare_at_price / 100);
      }
    });
    if (!isDefault) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url);
    }
  };

  form.addEventListener('change', (event) => {
    if (event.target.matches('input[type="radio"]')) render();
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const variant = currentVariant();
    if (!variant) {
      const missing = fieldsets.find((fs) => !qs('[data-fixed-option]', fs) && !qs('input:checked', fs));
      if (missing) {
        missing.classList.remove('is-missing');
        void missing.offsetWidth;
        missing.classList.add('is-missing');
        if (error) {
          error.textContent = strings.selectSize || 'Select a size';
          error.hidden = false;
        }
        qs('input[type="radio"]', missing)?.focus();
      }
      return;
    }
    if (!variant.available || button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      await api.cartAdd(new FormData(form));
      button.classList.remove('is-loading');
      button.classList.add('is-done');
      setTimeout(() => button.classList.remove('is-done'), 1400);
    } catch (err) {
      button.classList.remove('is-loading');
      if (error) {
        error.textContent = err.message || strings.cartError;
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  });

  // Remember the size someone picks and pre-select it on the next product (only if that size is in stock there).
  const SIZE_KEY = 'vorix.size';
  const sizeInputs = qsa('input[type="radio"]', form).filter((input) => /^size$/i.test(input.name));
  if (sizeInputs.length && !sizeInputs.some((input) => input.checked)) {
    let saved = null;
    try {
      saved = localStorage.getItem(SIZE_KEY);
    } catch (_) {
      /* storage blocked: start with no size */
    }
    const match = sizeInputs.find((input) => input.value === saved && !input.hasAttribute('data-unavailable'));
    if (match) match.checked = true;
  }
  form.addEventListener('change', (event) => {
    if (!sizeInputs.includes(event.target)) return;
    try {
      localStorage.setItem(SIZE_KEY, event.target.value);
    } catch (_) {
      /* not remembered, that's fine */
    }
  });

  render();
  form.setAttribute('data-ready', '');
}

/* ---------- Complete the set: add the matching piece with this one ---------- */
function initSet(section, form, api, strings) {
  const set = qs('[data-set]', section);
  if (!set || !form) return;
  const variants = JSON.parse(qs('[data-set-variants]', set)?.textContent || '[]');
  const sizeSelect = qs('[data-set-size]', set);
  const button = qs('[data-set-add]', set);
  const error = qs('[data-set-error]', set);
  const colourIndex = Number(set.dataset.colourIndex);
  const sizeIndex = Number(set.dataset.sizeIndex);
  const sizeInputs = qsa('input[type="radio"]', form).filter((input) => /^size$/i.test(input.name));
  const chosenSize = () => sizeInputs.find((input) => input.checked)?.value;
  // The matching piece follows the size picked above.
  const follow = () => {
    const size = chosenSize();
    if (size && sizeSelect && [...sizeSelect.options].some((o) => o.value === size)) sizeSelect.value = size;
  };
  form.addEventListener('change', follow);
  follow();
  const fail = (message) => {
    error.textContent = message;
    error.hidden = false;
  };
  button?.addEventListener('click', async () => {
    error.hidden = true;
    if (sizeInputs.length && !chosenSize()) {
      fail(strings.selectSize || 'Choose your size above first.');
      return;
    }
    const mainId = qs('[data-variant-id]', form)?.value;
    const size = sizeSelect?.value;
    const fits = (v) => v.available && (sizeIndex < 0 || v.options[sizeIndex] === size);
    // Same colour when the matching piece comes in it, otherwise its first colour (Black).
    const partner = variants.find((v) => fits(v) && colourIndex >= 0 && v.options[colourIndex] === section.dataset.colour) || variants.find(fits);
    if (!mainId || !partner) {
      fail('That size isn’t available in the matching piece.');
      return;
    }
    const body = new FormData();
    body.append('items[0][id]', mainId);
    body.append('items[0][quantity]', '1');
    body.append('items[1][id]', String(partner.id));
    body.append('items[1][quantity]', '1');
    button.classList.add('is-loading');
    button.disabled = true;
    try {
      await api.cartAdd(body);
    } catch (err) {
      fail(err.message || strings.cartError);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  });
}

/* ---------- Gift card: the picture follows the chosen amount ---------- */
// Each amount's variant points at its own card picture; only that one is shown (gallery and lightbox).
function initAmountGallery(section, form) {
  if (!form || !section.hasAttribute('data-amount-gallery')) return;
  const variants = JSON.parse(qs('[data-variants]', form)?.textContent || '[]');
  const items = qsa('[data-gallery-item]', section);
  const slides = qsa('[data-lightbox-slide]', section);
  const track = qs('[data-gallery-track]', section);
  const show = () => {
    const id = qs('[data-variant-id]', form)?.value;
    const media = variants.find((v) => String(v.id) === String(id))?.featured_media?.id;
    const match = (el) => el.dataset.mediaId === String(media);
    if (!media || !items.some(match)) return;
    items.forEach((el) => (el.hidden = !match(el)));
    slides.forEach((el) => (el.hidden = !match(el)));
    track?.scrollTo({ left: 0, behavior: 'auto' });
  };
  // After initForm's own change handler has set the new variant id.
  form.addEventListener('change', () => requestAnimationFrame(show));
}

/* ---------- Colour: a swatch shows that colour's front and back photos ---------- */
// Works before Zero Hour too: the locked view has its own swatches, kept in step with the buy form's.
function initColours(section, form, api) {
  if (!qs('[data-colour-picker]', section)) return;
  const gallery = qs('[data-gallery]', section);
  const track = qs('[data-gallery-track]', section);
  const items = qsa('[data-gallery-item]', section);
  const slides = qsa('[data-lightbox-slide]', section);
  const names = qsa('[data-colour-name]', section);
  const viewLabel = gallery?.dataset.viewLabel || '';
  const colourIndex = Number(section.dataset.colourIndex);
  let variants = [];
  try {
    variants = JSON.parse(qs('[data-variants]', section)?.textContent || '[]');
  } catch (_) {
    /* no variant data: the URL just stays as it is */
  }
  let selected = section.dataset.colour || qs('[data-colour-input]:checked', section)?.value || '';

  // The variant for this colour, keeping any size already picked in the buy form.
  const variantFor = (colour) => {
    const chosen = form ? qsa('fieldset.opt:not([data-print-picker])', form).map((fs) => qs('input[type="radio"]:checked', fs)?.value ?? qs('[data-fixed-option]', fs)?.value ?? null) : [];
    const ofColour = variants.filter((v) => v.options[colourIndex] === colour);
    return ofColour.find((v) => v.options.every((value, i) => i === colourIndex || chosen[i] == null || chosen[i] === value)) || ofColour[0];
  };

  const show = (colour, { initial = false } = {}) => {
    selected = colour;
    section.dataset.colour = colour;
    qsa('[data-colour-input]', section).forEach((input) => (input.checked = input.value === colour));
    names.forEach((el) => (el.textContent = colour));

    const matches = (el) => el.dataset.colour === '*' || el.dataset.colour === colour;
    const shown = items.filter(matches);
    items.forEach((item) => (item.hidden = !matches(item)));
    slides.forEach((slide) => (slide.hidden = !matches(slide)));
    shown.forEach((item, i) => {
      const zoom = qs('[data-zoom]', item);
      if (zoom && viewLabel) zoom.setAttribute('aria-label', viewLabel.replace('[index]', i + 1).replace('[count]', shown.length));
    });
    gallery?.classList.toggle('gallery--single', shown.length <= 1);
    qsa('[data-gallery-total], [data-lightbox-total]', section).forEach((el) => (el.textContent = String(shown.length)));
    const ui = qs('[data-gallery-ui]', section);
    if (ui) ui.hidden = shown.length <= 1;
    qsa('[data-lightbox-prev], [data-lightbox-next]', section).forEach((btn) => (btn.hidden = shown.length <= 1));

    const image = (shown.find((i) => i.dataset.photoView === 'front') || shown[0])?.dataset.cardImage;
    const variant = variantFor(colour);
    const url = variant ? `${section.dataset.url}?variant=${variant.id}` : section.dataset.url;
    qsa('[data-save]', section).forEach((btn) => {
      btn.dataset.colour = colour;
      btn.dataset.url = url;
      if (image) btn.dataset.image = image;
    });
    api.syncSaved?.();
    if (initial) return;

    const current = qs('[data-gallery-current]', section);
    if (current) current.textContent = '1';
    const bar = qs('[data-gallery-bar]', section);
    bar?.style.setProperty('--count', String(shown.length));
    bar?.style.setProperty('--progress', '0');
    if (track) track.scrollTo({ left: 0, behavior: 'auto' });
    if (variant) {
      const next = new URL(window.location.href);
      next.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', next);
    }
  };

  section.addEventListener('change', (event) => {
    if (event.target.matches('[data-colour-input]')) show(event.target.value);
  });
  // Hovering a swatch previews its name; leaving puts the chosen colour back.
  qsa('.swatch[data-swatch-name]', section).forEach((swatch) => {
    swatch.addEventListener('pointerenter', () => names.forEach((el) => (el.textContent = swatch.dataset.swatchName)));
    swatch.addEventListener('pointerleave', () => names.forEach((el) => (el.textContent = selected)));
  });
  show(selected, { initial: true });
}

/* ---------- Mobile sticky purchase bar ---------- */
function initBuybar(section, form) {
  const bar = qs('[data-buybar]', section);
  const anchors = qsa('[data-buy-button]', section);
  if (!bar || !anchors.length) return;
  const visibleAnchor = () => anchors.find((el) => el.offsetParent !== null) || anchors[0];
  const footer = qs('.section-footer, footer');
  let shown = false;
  let frame = 0;
  // Measured on every scroll frame rather than via IntersectionObserver, so an instant jump
  // (e.g. tapping the iOS status bar to scroll to top) can't leave the bar stuck open.
  const sync = () => {
    frame = 0;
    const pastButton = visibleAnchor().getBoundingClientRect().bottom < 0;
    // Step aside once the footer (with its own sign-up) fills a good part of the screen.
    const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight * 0.6 : false;
    const show = pastButton && !footerVisible;
    if (show === shown) return;
    shown = show;
    bar.classList.toggle('is-visible', show);
    bar.toggleAttribute('inert', !show);
    bar.setAttribute('aria-hidden', String(!show));
  };
  const schedule = () => (frame ||= requestAnimationFrame(sync));
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  sync();
  const action = qs('[data-buybar-action]', bar);
  if (action && form) {
    action.addEventListener('click', () => {
      const button = qs('[data-buy-button]', form);
      if (button?.dataset.state === 'choose') {
        qs('[data-buy]', section)?.scrollIntoView({ behavior: window.vorix.api.motion() === 'off' ? 'auto' : 'smooth', block: 'center' });
        setTimeout(() => qs('fieldset.opt input[type="radio"]', form)?.focus({ preventScroll: true }), 400);
        return;
      }
      form.requestSubmit();
    });
  }
}
